import { useState, useEffect } from "react";
import { openDB } from "idb";
import { toast } from "sonner";
import axios from "axios";
import { API } from "@/config/constants";

const initDB = async () => {
  return openDB("NatureJournalDB", 1, {
    upgrade(db) {
      if (!db.objectStoreNames.contains("offlineEntries")) {
        db.createObjectStore("offlineEntries", { keyPath: "id" });
      }
      if (!db.objectStoreNames.contains("settings")) {
        db.createObjectStore("settings", { keyPath: "key" });
      }
    },
  });
};

const saveOfflineEntry = async (entry) => {
  const db = await initDB();
  const offlineEntry = {
    id: `offline-${Date.now()}`,
    data: entry,
    apiUrl: process.env.REACT_APP_BACKEND_URL,
    createdAt: new Date().toISOString(),
    synced: false
  };
  await db.put("offlineEntries", offlineEntry);
  return offlineEntry;
};

const getOfflineEntries = async () => {
  const db = await initDB();
  return db.getAll("offlineEntries");
};

const deleteOfflineEntry = async (id) => {
  const db = await initDB();
  await db.delete("offlineEntries", id);
};

const syncOfflineEntries = async () => {
  const offlineEntries = await getOfflineEntries();
  let syncedCount = 0;

  for (const entry of offlineEntries) {
    try {
      await axios.post(`${API}/entries`, entry.data);
      await deleteOfflineEntry(entry.id);
      syncedCount++;
    } catch (error) {
      console.error("Failed to sync entry:", entry.id);
    }
  }

  return syncedCount;
};

const useOnlineStatus = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      syncOfflineEntries().then(count => {
        if (count > 0) {
          toast.success(`Synced ${count} offline entries!`);
        }
      });
    };
    const handleOffline = () => setIsOnline(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  return isOnline;
};

export { initDB, saveOfflineEntry, getOfflineEntries, deleteOfflineEntry, syncOfflineEntries, useOnlineStatus };
